import _ from 'lodash';
import { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import { convertHelpers } from '../../common/convert';
import { gEnums } from '../../enums/globalEnums';
import { timeStampsHelpers } from '../../firestoreData/updates/subUpdates/fsTimeStamps';
import { eventInfoHandlers, eventInfoInitialState, eventInfoReducer } from '../reducers/EventInfoReducer';
import { _cacheTypes } from '../reducers/ServiceWorkerReducer';
import { AppContext } from './AppProvider';
import { AppSettingsContext } from './AppSettingsContext';
import { AppUsersContext } from './AppUsersContext';
import { DatabaseContext } from './DatabaseContext';
import { DemoContext } from './DemoContext';
import { GoogleSheetsContext } from './GoogleSheetsContext';
import { PapsContext } from './PapsContext';
import { RegDataContext } from './RegDataContext';
import { _useCache, ServiceWorkerContext } from './ServiceWorkerContext';
import { CurrentSettingsContext } from './SettingsDataContext';
import { StartContext } from './StartContext';
import { useEventInfo } from './useHelpers/useEventInfo';
import { useNotificationsInfo } from './useHelpers/useNotificationsInfo';
import { useAppUsersInfo } from './useHelpers/useAppUsersInfo';

/** gets the data for the uiItem.
 * @returns viewListData, confirmation */
export const EventInfoContext = createContext();

/** gets the data for the uiItem.
 * @returns viewListData,  
 * @returns confirmation,  */
const EventInfoProvider = (props) => {

  // ParentContext not available

  const aat = gEnums.appAreaTypes
  const svc = [aat.clients, aat.clubs, aat.districts, aat.event, aat.leagues, aat.schools, aat.organizations, aat.client]

  // appContext
  const appContext = useContext(AppContext)
  const { app_state } = appContext
  const { mainDb, mainStorage } = app_state ?? {}

  // currentSettingsContext
  const currentSettingsContext = useContext(CurrentSettingsContext)
  const { currentSettings_state } = currentSettingsContext ?? {}
  const { aps_global, aps_viewItems, aps_appUserSettings } = _.get(currentSettings_state, 'gvviSettings', {});

  // startContext
  const startContext = useContext(StartContext)
  const { start_handlers } = startContext ?? {}

  // previewContext
  const demoContext = useContext(DemoContext)
  const { demo_state } = demoContext ?? {}

  // googleSheetsContext
  const googleSheetsContext = useContext(GoogleSheetsContext)
  const { googleSheets_state } = googleSheetsContext ?? {}

  // appUsersContext
  const regDataContext = useContext(RegDataContext);
  const { regData_state } = regDataContext ?? {}
  const { regInfo, eventKey } = regData_state ?? {}

  // databaseContext
  const databaseContext = useContext(DatabaseContext)
  const { database_fns } = databaseContext ?? {}

  // appUsersContext
  const appUsersContext = useContext(AppUsersContext);
  const { appUsers_handlers } = appUsersContext

  // papsContext
  const papsContext = useContext(PapsContext);
  const { paps_state } = papsContext
  const { appArea, pathViews, cacheKey, pathModes } = paps_state ?? {}

  // serviceWorkerContext
  const serviceWorkerContext = useContext(ServiceWorkerContext)
  const { serviceWorker_state, serviceWorker_handlers } = serviceWorkerContext ?? {}
  const { cache_info } = serviceWorker_state ?? {}
  const { cache_groups } = cache_info ?? {}
  const { cache_event } = cache_groups ?? {}
  const cache_currentEvent = cache_event && pathViews && pathViews.events ? cache_event[pathViews.events] : {}
  const { cache_collections: _cache_collections, _timeStamps: _timeStamps_cache } = cache_currentEvent ?? {}

  // appSettingsContext
  const appSettingsContext = useContext(AppSettingsContext);
  const { appSettings_state, appSettings_handlers } = appSettingsContext ?? {}
  const { timeStamps_event_db } = appSettings_state ?? {}

  const isOrganization = pathViews.organizations ? true : false

  // pageContext 
  const { appDataSource } = aps_global ?? {}
  const { appUserCollection, appUserCollections, allowPresenceTracking } = aps_appUserSettings ?? {}
  const { useStartDataCollections, getAllDataCollections, startDataCollections } = appDataSource ?? {}

  const _appUserCollections = appUserCollections ? appUserCollections : []
  if (_appUserCollections.length === 0 && appUserCollection) { _appUserCollections.push(appUserCollection) }

  // eventInfoInitialState
  const initState = eventInfoInitialState({ mainDb, mainStorage, pathViews, database_fns, appDataSource, aps_viewItems })
  const [eventInfo_state, dispatch] = useReducer(eventInfoReducer, initState);
  const eventInfo_handlers = useMemo(
    () => eventInfoHandlers(dispatch),
    [dispatch]
  );

  const eventInfo = useEventInfo()
  const notificationsInfo = useNotificationsInfo()
  const appUsersInfo = useAppUsersInfo()

  const infoList = [eventInfo, notificationsInfo, appUsersInfo];

  useEffect(() => {
    if (!pathViews.events) return;
    infoList.forEach((info) => {
      if (info) eventInfo_handlers.handleInit_eventInfo(info);
    });
    // eslint-disable-next-line
  }, [eventInfo, notificationsInfo, appUsersInfo]);

  const { eventData, staticViews, staticEventKey, appDataDocuments, appGlobalData, appGlobalDataFromCache, clientProfiles } = eventInfo_state ?? {}
  const { _itemKey: eventItemKey, name: eventName } = eventData ?? {}

  const getStaticName = (staticView, staticValue, returnFound) => {
    const _staticView = staticView === 'guests' ? 'attendees' : staticView
    if (staticViews && staticViews[_staticView] && staticViews[_staticView][staticValue]) {
      const svv = staticViews[_staticView][staticValue]
      if (returnFound) {
        return { sv: convertHelpers.getItemName(svv) }
      } else {
        return convertHelpers.getItemName(svv)
      }
    } else {
      if (returnFound) {
        return { sv: staticValue, notFound: true }
      } else {
        return staticValue
      }
    }
  }

  useEffect(
    () => {
      if (demo_state?.demoOn && demo_state?.demoPreview) {
        eventInfo_handlers.handleSet_demoData(demo_state.demoPreview)
      }
    },
    // eslint-disable-next-line
    [demo_state]
  )

  useEffect(
    () => {
      const cache_eventInfo = {
        cache_collection_timeStampsInfo: {},
        ...cache_currentEvent
      }

      // get the cache_collection_timeStampsInfo from cache
      const { cache_collections, _timeStamps } = cache_eventInfo

      _.forEach(cache_collections, (cache_collection, collectionKey) => {

        const eventTimeStamp_db = timeStamps_event_db && timeStamps_event_db[collectionKey]
        const eventTimeStamp_cache = _timeStamps && _timeStamps[collectionKey]

        if (eventTimeStamp_db && eventTimeStamp_cache) {
          cache_eventInfo.cache_collection_timeStampsInfo[collectionKey] = {
            cacheUpdateNeeded: eventTimeStamp_db !== eventTimeStamp_cache,
          }
        } else {
          cache_eventInfo.cache_collection_timeStampsInfo[collectionKey] = {
            cacheUpdateNeeded: true,
            timeStampUpdateNeeded: true,
          }
        }
      });

      start_handlers.handleAmmend_useEffect('handleSet_eventCacheData')
      eventInfo_handlers.handleSet_eventCacheData(pathViews, cache_eventInfo)
    },
    // eslint-disable-next-line
    []
  )

  useEffect(
    () => {
      if (!_useCache.events) { return }
      if (!appGlobalDataFromCache && appGlobalData) {
        const settings = {
          cacheKey: cacheKey,
          cacheName: _cacheTypes.appGlobalData,
          cacheData: appGlobalData,
        }
        serviceWorker_handlers.handleSend_messageToServiceWorker(settings);
      }
    },
    // eslint-disable-next-line
    [appGlobalData]
  )

  useEffect(
    () => {
      if (regInfo?.eventKey === pathViews.events && regInfo?.appDataDocuments) {
        eventInfo_handlers.handleSet_appDataDocuments(regInfo.appDataDocuments, null, true)
      } else {
        eventInfo_handlers.handleClear_regDataDocuments()
      }
    },
    // eslint-disable-next-line
    [regInfo]
  )

  useEffect(
    () => {
      if (clientProfiles) {
        eventInfo_handlers.handleSet_appUserUrls(clientProfiles, appUsersInfo)
      }
    },
    // eslint-disable-next-line
    [clientProfiles]
  )

  useEffect(
    () => {
      if (eventName) {
        appSettings_handlers && appSettings_handlers.handleAmmend_appHistory(eventName, paps_state?.pathName)
      }
    },
    // eslint-disable-next-line
    [eventItemKey, eventName]
  )
  useEffect(
    () => {
      if (appDataDocuments) {
        if (appUserCollection && appDataDocuments[appUserCollection]) {
          appUsers_handlers.handleSet_eventAppUsers(appDataDocuments[appUserCollection])
        }
      }
    },
    // eslint-disable-next-line
    [appDataDocuments]
  )

  const eventInfo_fns = {
    getStaticName: (staticView, staticValue, returnFound) => getStaticName(staticView, staticValue, returnFound)
  }

  if (svc.includes(appArea)) {
    if (staticEventKey) {
      return <EventInfoContext.Provider value={{ eventInfo_state, eventInfo_handlers, eventInfo_fns }}>
        {props.children}
      </EventInfoContext.Provider>
    } else {
      return <EventInfoContext.Provider value={{ eventInfo_state, eventInfo_handlers, eventInfo_fns }}>
        {props.children}
      </EventInfoContext.Provider>
    }
  } else {
    return <EventInfoContext.Provider value={{ eventInfo_state, eventInfo_handlers, eventInfo_fns }}>
      {props.children}
    </EventInfoContext.Provider>
  }
}

export default EventInfoProvider 